import * as React from 'react';
import notify from '../../shared/feedback/notifications';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import * as Features from '../../shared/Features';
import { Translate } from '../../shared/LanguageService';
import FormInputChangeHandler from '../../shared/form/FormInputChangeHandler';
import ISe360User from '../se360users/ISe360User';
import StatisticsList from './list/StatisticsList';
import StatisticsFilter from './new/StatisticsFilter';
import * as actions from './actions/StatisticsActions';
import StatisticsGrouping from './new/StatisticsGrouping';
import StatisticsSelection from './new/StatisticsSelection';
import { ISelections, IFilter, IGrouping } from './Entities';
import moment from 'moment';

interface IProps {
    selections: any;
    filters: any;
    list: any;
    actions: IActions;
    user: ISe360User;
}

interface IState {
    selections: ISelections;
    filters: IFilter;
    grouping: IGrouping;

    isSearching: false;
    isExporting: false;
    hasDoneSearch: false;
}

class StatisticsPage extends React.Component<IProps, IState> {
    private formInputChangeHandler: FormInputChangeHandler;

    constructor(props: IProps) {
        super(props);

        let now = moment();
        let today = moment(new Date(now.year(), now.month(), now.date())).toDate();
        this.state = {
            selections: {
                numberOfOrders: false,
                totalIncome: false,
                totalPhotographerCost: false,
                totalExternalCost: false,
                amountInOut: Features.hasFeature(Features.ViewEconomicStatistics, props.user),
                hideUnusedProducts: false,
                showNumberOfUniqueProducts: false,
                numberOfPhotoOrders: false,
                numberOfPhotoSubOrders: false,
                numberOfImages: false,
                numberOfBookDirectOrders: false,
                customerDirectOrders: false,
                numberOfPlanOrders: false,
                numberOfUploadedPlanOrders: false,
                numberOfPlanStoreys: false,
                numberOfRoomSketcherPlanStoreys: false,
                numberOfSketchupPlanStoreys: false,
                selectedProducts: [],
                selectedProductGroup: null
            },
            filters: {
                region: null,
                photographer: null,
                chain: null,
                firm: null,
                seller: null,
                contentProvider: null,
                invoicedOnly: false,
                includeRemoved: false,
                compareWithLastYear: false,
                periodDates: [{ start: today, end: today }]
            },
            grouping: {
                isFirm: false,
                perUser: false,
                isPhotographer: false,
                isPeriod: true,
                period: {
                    periodly: true,
                    monthly: false,
                    quarterly: false
                }
            },
            isSearching: false,
            isExporting: false,
            hasDoneSearch: false
        };

        this.onGroupingRadioChanged = this.onGroupingRadioChanged.bind(this);
        this.onGroupingPeriodRadioChanged = this.onGroupingPeriodRadioChanged.bind(this);
        this.search = this.search.bind(this);
        this.exportExcel = this.exportExcel.bind(this);
        this.getSearchData = this.getSearchData.bind(this);
        this.onProductGroupChanged = this.onProductGroupChanged.bind(this);

        this.formInputChangeHandler = new FormInputChangeHandler(this.state, (newFilter: IState) =>
            this.setState(newFilter)
        );
    }

    componentDidMount() {
        this.props.actions.newSearch();
    }

    componentWillUnmount() {
        this.props.actions.resetState();
    }

    onDateTimeChange(value: any, name: string) {
        let date = value ? value.toDate() : null;
        this.formInputChangeHandler.handleChange(name, date);
    }

    onGroupingRadioChanged(e: any) {
        this.formInputChangeHandler.handleChange('grouping.isFirm', false);
        this.formInputChangeHandler.handleChange('grouping.isPhotographer', false);
        this.formInputChangeHandler.handleChange('grouping.isPeriod', false);

        this.formInputChangeHandler.onChange(e);
    }

    onGroupingPeriodRadioChanged(e: any) {
        this.formInputChangeHandler.handleChange('grouping.period.periodly', false);
        this.formInputChangeHandler.handleChange('grouping.period.monthly', false);
        this.formInputChangeHandler.handleChange('grouping.period.quarterly', false);

        this.formInputChangeHandler.onChange(e);
    }

    search() {
        this.formInputChangeHandler.handleChange('isSearching', true);
        this.props.actions
            .search(this.getSearchData())
            .then(() => {
                this.formInputChangeHandler.handleChange('isSearching', false);
                this.formInputChangeHandler.handleChange('hasDoneSearch', true);
            })
            .catch(() => {
                this.formInputChangeHandler.handleChange('isSearching', false);
            });
    }

    exportExcel() {
        this.formInputChangeHandler.handleChange('isExporting', true);
        this.props.actions
            .exportExcel(this.getSearchData())
            .then((data) => {
                this.formInputChangeHandler.handleChange('isExporting', false);

                if (!data) {
                    notify.warning(Translate('statistics/page/export/toastr/no-results'));
                }
            })
            .catch(() => {
                this.formInputChangeHandler.handleChange('isExporting', false);
                notify.failure(Translate('statistics/page/export/toastr/error'));
            });
    }

    getSearchData() {
        return {
            selections: this.state.selections,
            filters: this.state.filters,
            grouping: this.state.grouping
        };
    }

    onProductGroupChanged(e: any) {
        let selectedProducts: string[] = [];

        if (e.target.value) {
            this.props.selections.groupedProducts.forEach((x) => {
                x.products.forEach((p) => {
                    if (p.productGroupIds.some((y) => y.toString() === e.target.value.toString())) {
                        selectedProducts.push(p.id.toString());
                    }
                });
            });
        }

        this.formInputChangeHandler.onChange(e);
        this.formInputChangeHandler.handleChange(
            'selections.selectedProducts',
            selectedProducts.map((x) => x)
        );
    }

    render() {
        return (
            <div className="statistics-page">
                <h1>{Translate('statistics/page/title')}</h1>
                <StatisticsSelection
                    numberOfOrders={this.state.selections.numberOfOrders}
                    totalIncome={this.state.selections.totalIncome}
                    totalPhotographerCost={this.state.selections.totalPhotographerCost}
                    totalExternalCost={this.state.selections.totalExternalCost}
                    amountInOut={this.state.selections.amountInOut}
                    hideUnusedProducts={this.state.selections.hideUnusedProducts}
                    showNumberOfUniqueProducts={this.state.selections.showNumberOfUniqueProducts}
                    numberOfPhotoOrders={this.state.selections.numberOfPhotoOrders}
                    numberOfPhotoSubOrders={this.state.selections.numberOfPhotoSubOrders}
                    numberOfImages={this.state.selections.numberOfImages}
                    numberOfBookDirectOrders={this.state.selections.numberOfBookDirectOrders}
                    customerDirectOrders={this.state.selections.customerDirectOrders}
                    numberOfPlanOrders={this.state.selections.numberOfPlanOrders}
                    numberOfUploadedPlanOrders={this.state.selections.numberOfUploadedPlanOrders}
                    numberOfPlanStoreys={this.state.selections.numberOfPlanStoreys}
                    numberOfRoomSketcherPlanStoreys={
                        this.state.selections.numberOfRoomSketcherPlanStoreys
                    }
                    numberOfSketchupPlanStoreys={this.state.selections.numberOfSketchupPlanStoreys}
                    groupedProducts={this.props.selections.groupedProducts}
                    selectedProducts={this.state.selections.selectedProducts}
                    productGroups={this.props.selections.productGroups}
                    selectedProductGroup={this.state.selections.selectedProductGroup}
                    onProductGroupChanged={this.onProductGroupChanged}
                    handleMultiselectChange={this.formInputChangeHandler.onChange}
                    handleCheckboxChange={this.formInputChangeHandler.onChange}
                    user={this.props.user}
                />
                <StatisticsFilter
                    region={this.state.filters.region}
                    photographer={this.state.filters.photographer}
                    chain={this.state.filters.chain}
                    firm={this.state.filters.firm}
                    seller={this.state.filters.seller}
                    contentProvider={this.state.filters.contentProvider}
                    periodDates={this.state.filters.periodDates}
                    invoicedOnly={this.state.filters.invoicedOnly}
                    includeRemoved={this.state.filters.includeRemoved}
                    compareWithLastYear={this.state.filters.compareWithLastYear}
                    regions={this.props.filters.regions}
                    photographers={this.props.filters.photographers}
                    chains={this.props.filters.chains}
                    firms={this.props.filters.firms}
                    sellers={this.props.filters.sellers}
                    contentProviderOptions={this.props.filters.contentProviderOptions}
                    changeValue={this.formInputChangeHandler.onChange}
                    handleCheckboxChange={this.formInputChangeHandler.onChange}
                    onDateTimeChange={(e, name) => this.onDateTimeChange(e, name)}
                />
                <StatisticsGrouping
                    grouping={this.state.grouping}
                    onChange={this.formInputChangeHandler.onChange}
                    handleChange={this.formInputChangeHandler.handleChange}
                    onGroupingRadioChanged={this.onGroupingRadioChanged}
                    onGroupingPeriodRadioChanged={this.onGroupingPeriodRadioChanged}
                />
                <Button bsStyle="success" onClick={this.search} disabled={this.state.isSearching}>
                    <i className="material-icons md-18">search</i>{' '}
                    {Translate('statistics/page/search-button')}{' '}
                    {this.state.isSearching && <i className="load-spin-dark"></i>}
                </Button>
                <Button bsStyle="info" onClick={this.exportExcel} disabled={this.state.isExporting}>
                    <i className="material-icons md-18">cloud_download</i>{' '}
                    {Translate('statistics/page/export-excel-button')}{' '}
                    {this.state.isExporting && <i className="load-spin-dark"></i>}
                </Button>
                <StatisticsList list={this.props.list} hasDoneSearch={this.state.hasDoneSearch} />
            </div>
        );
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        filters: {
            regions: state.statistics.newSearch.availableRegions,
            chains: state.statistics.newSearch.availableChains,
            firms: state.statistics.newSearch.availableFirms,
            photographers: state.statistics.newSearch.availablePhotographers,
            sellers: state.statistics.newSearch.availableSellers,
            contentProviderOptions: state.statistics.newSearch.availableContentProviders
        },
        selections: {
            groupedProducts: state.statistics.newSearch.groupedProducts,
            productGroups: state.statistics.newSearch.availableProductGroups
        },
        list: state.statistics.search,
        user: state.user
    };
}

function mapDispatchToProps(dispatch: any): { actions: IActions } {
    return {
        actions: {
            newSearch: () => dispatch(actions.newSearch()),
            search: (data: any) => dispatch(actions.search(data)),
            exportExcel: (data: any) => dispatch(actions.exportExcel(data)),
            resetState: () => dispatch(actions.resetState())
        }
    };
}

interface IActions {
    newSearch: () => Promise<any>;
    search: (data: any) => Promise<any>;
    exportExcel: (data: any) => Promise<any>;
    resetState: () => Promise<any>;
}

export default connect(mapStateToProps, mapDispatchToProps)(StatisticsPage);
